import React, { SVGProps } from "react";

type IconType = React.ComponentType<SVGProps<SVGSVGElement>> | string;

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string;
  leftIcon?: IconType;
  rightIcon?: IconType;
  target?: "_blank" | "_self" | "_parent" | "_top";
}

const ButtonBase: React.FC<Props> = ({
  children,
  href,
  target = "_blank",
  leftIcon,
  rightIcon,
  className,
  onClick,
  ...buttonSpecificProps
}) => {
  const renderIcon = (icon: IconType) => {
    if (typeof icon === "string") {
      return <img src={icon} alt="icon" className="inline-block" />;
    } else {
      return React.createElement(icon, { className: "inline-block" });
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (href) {
      event.preventDefault();
      if (target === "_blank") {
        window.open(href, target);
      } else {
        const anchor = document.createElement("a");
        anchor.href = href;
        anchor.target = target;
        anchor.click();
      }
    } else if (onClick) {
      onClick(event);
    }
  };

  return (
    <button
      {...buttonSpecificProps}
      onClick={handleClick}
      className={`inline-flex items-center justify-center py-2 px-4 ${className || ""}`.trim()}
    >
      {leftIcon && <span className="mr-2">{renderIcon(leftIcon)}</span>}
      <span className="flex-1 text-center overflow-hidden text-nowrap text-ellipsis">{children}</span>
      {rightIcon && <span className="ml-2">{renderIcon(rightIcon)}</span>}
    </button>
  );
};

export const PrimaryButton: React.FC<Props> = ({ children, ...props }) => {
  return (
    <ButtonBase
      {...props}
      className={`primary-button self-center
        disabled:bg-white disabled:text-neutral-400 disabled:border-neutral-400 disabled:cursor-not-allowed
        bg-primary-blue-560 border border-primary-blue-560 py-2 px-4 rounded text-white font-semibold hover:bg-primary-blue-460 active:bg-primary-blue-660 focus:bg-primary-blue-460 focus:outline-none focus:ring focus:ring-primary-blue-560 focus:ring-offset-1" ${props.className}`}
    >
      {children}
    </ButtonBase>
  );
};

export const SecondaryButton: React.FC<Props> = ({ children, ...props }) => {
  return (
    <ButtonBase
      {...props}
      className={`secondary-button self-center bg-white border border-neutral-400 py-2 px-4 rounded text-neutral-850 font-semibold hover:bg-neutral-50 active:bg-neutral-100 focus:bg-neutral-50 focus:outline-none focus:ring focus:ring-primary-blue-560 focus:ring-offset-1" ${props.className}`}
    >
      {children}
    </ButtonBase>
  );
};

export const SecondarySubtleButton: React.FC<Props> = ({ children, ...props }) => {
  return (
    <ButtonBase
      {...props}
      className={`secondary-subtle-button self-center bg-transparent border border-transparent py-2 px-4 rounded text-neutral-850 font-semibold hover:bg-neutral-50 hover:border-bg-neutral-50 active:bg-neutral-100 active:border-bg-neutral-100 focus:bg-neutral-50 focus:border-neutral-50 focus:outline-none focus:ring focus:ring-primary-blue-560 focus:ring-offset-1 ${props.className}`}
    >
      {children}
    </ButtonBase>
  );
};

export const InlineSubtleButton: React.FC<Props> = ({ children, ...props }) => {
  return (
    <ButtonBase
      className={`secondary-subtle-button self-center bg-transparent border border-transparent py-2 px-4 rounded text-neutral-850 font-semibold hover:bg-neutral-50 hover:border-bg-neutral-50 active:bg-neutral-100 active:border-bg-neutral-100 focus:bg-neutral-50 focus:border-neutral-50 focus:outline-none focus:ring focus:ring-primary-blue-560 focus:ring-offset-1" ${props.className}`}
      {...props}
    >
      {children}
    </ButtonBase>
  );
};

export const IconButton: React.FC<Props> = ({ children, ...props }) => {
  return (
    <ButtonBase
      {...props}
      leftIcon={props.leftIcon}
      rightIcon={null}
      className={`secondary-subtle-button self-center bg-transparent border border-transparent py-2 px-4 rounded text-neutral-850 font-semibold hover:bg-neutral-50 hover:border-bg-neutral-50 active:bg-neutral-100 active:border-bg-neutral-100 focus:bg-neutral-50 focus:border-neutral-50 focus:outline-none focus:ring focus:ring-primary-blue-560 focus:ring-offset-1" ${props.className}`}
    >
      {children}
    </ButtonBase>
  );
};
